import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useState, type MouseEvent, type ReactElement } from "react";
import { useLocation } from "wouter";
import { CdnImage } from "./common/CdnImage";
import type { ImageProps } from "../types";
import { resolveAssetUrl } from "../utils/resolveAssetUrl";

export interface FlipCardGridCard {
  title: string;
  body: string;
  image?: ImageProps;
  badge?: string;
  release?: string;
  href?: string;
}

interface FlipCardGridProps {
  cards: FlipCardGridCard[];
  title: string;
  columns?: 2;
}

export default function FlipCardGrid({ cards, title, columns }: FlipCardGridProps): ReactElement {
  const [flippedCards, setFlippedCards] = useState<Set<number>>(() => new Set());
  const [, setLocation] = useLocation();

  function isInternalRoute(href: string): boolean {
    return href.startsWith("/") && !href.startsWith("//");
  }

  function toggleCard(index: number): void {
    setFlippedCards((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  return (
    <Paper
      component="section"
      aria-label="Octant applets"
      elevation={0}
      sx={{
        "--roadmap-brand-a": "#6d3b78",
        "--roadmap-brand-b": "#bb90c5",
        "--roadmap-card": "#fff",
        "--roadmap-card-border": "#e7ebf1",
        "--roadmap-ink": "#14161c",
        "--roadmap-soft": "#565f70",
        "--roadmap-divider": "#eef1f5",
        "--roadmap-hint": "#aab2c0",
        "--roadmap-card-shadow": "rgba(20,22,28,.5)",
        width: "min(980px, 100%)",
        mx: "auto",
        my: { sm: 3, md: 5 },
        px: "clamp(20px, 5vw, 44px)",
        pt: 4,
        pb: 5.5,
        bgcolor: "transparent",
        borderRadius: "24px",
        boxShadow: "none",
        color: "var(--roadmap-ink)",
        "@media (prefers-color-scheme: dark)": {
          "--roadmap-card": "#131826",
          "--roadmap-card-border": "#262d3f",
          "--roadmap-ink": "#e9edf5",
          "--roadmap-soft": "#a7b0c2",
          "--roadmap-divider": "#1b202c",
          "--roadmap-hint": "#5c6478",
          "--roadmap-card-shadow": "rgba(0,0,0,.6)",
        },
      }}
    >
      <Typography
        component="h2"
        sx={{
          mb: 3.75,
          color: "rgba(233, 237, 245, 0.96)",
          fontFamily: '"Geist", Helvetica, Arial, sans-serif',
          fontSize: { sm: "1.6rem", md: "2.25rem" },
          fontWeight: 600,
          lineHeight: 1.2,
          textAlign: "center",
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: columns === 2 ? { sm: "1fr", md: "repeat(2, minmax(0, 1fr))" } : "repeat(auto-fit, minmax(min(210px, 100%), 1fr))", gap: columns === 2 ? { sm: 2.75, md: 4 } : 2.75, justifyItems: "center" }}>
        {cards.map((card, index) => {
          const isFlipped = flippedCards.has(index);
          const descriptionId = `applet-card-${index.toString()}-description`;
          const isLinked = typeof card.href === "string" && card.href.length > 0;
          const isInternalLinkedRoute = isLinked && isInternalRoute(card.href);

          return (
            <Box key={`${card.title}-${index.toString()}`} sx={{ width: "100%", display: "flex", justifyContent: "center", perspective: "1200px" }}>
              <ButtonBase
                aria-expanded={isFlipped}
                aria-controls={descriptionId}
                aria-label={isLinked ? `${card.title} — open applet page` : `${card.title} — flip for description`}
                component={isLinked ? "a" : "button"}
                href={isLinked ? card.href : undefined}
                onClick={isLinked
                  ? (event: MouseEvent<HTMLAnchorElement>) => {
                      if (isInternalLinkedRoute) {
                        event.preventDefault();
                        setLocation(card.href);
                      }
                    }
                  : () => { toggleCard(index); }}
                sx={{
                  display: "block",
                  width: "100%",
                  maxWidth: columns === 2 ? 360 : 240,
                  height: 300,
                  borderRadius: "18px",
                  color: "inherit",
                  outlineOffset: "4px",
                  "&:focus-visible": { outline: "3px solid var(--roadmap-brand-b)" },
                  "&:hover > .MuiBox-root": { transform: "rotateY(180deg)" },
                }}
              >
                <Box sx={{ position: "relative", width: "100%", height: "100%", transformStyle: "preserve-3d", willChange: "transform", transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)", transition: "transform 700ms cubic-bezier(.2,.7,.2,1)", "@media (prefers-reduced-motion: reduce)": { transitionDuration: "1ms" } }}>
                  <Paper elevation={0} sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden", p: 0, bgcolor: "var(--roadmap-card)", border: "1px solid var(--roadmap-card-border)", borderRadius: "18px", backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", boxShadow: "0 16px 40px -22px var(--roadmap-card-shadow)", transition: "background 200ms ease, border-color 200ms ease" }}>
                    {card.image?.src && (
                      <CdnImage
                        src={resolveAssetUrl(card.image.src)}
                        alt={card.image.alt ?? `${card.title} logo`}
                        sx={{ width: "77.5%", maxHeight: "57.5%", objectFit: "contain" }}
                      />
                    )}
                    {!card.image?.src && (
                      <Typography component="span" sx={{ fontFamily: 'Geist, Helvetica, Arial, sans-serif', fontSize: { sm: "1.65rem", md: "2rem" }, fontWeight: 700, background: "linear-gradient(150deg, var(--roadmap-brand-a), var(--roadmap-brand-b))", backgroundClip: "text", color: "transparent" }}>
                        {card.title}
                      </Typography>
                    )}
                    {card.release && (
                      <Typography component="span" sx={{ fontFamily: 'Geist, Helvetica, Arial, sans-serif', fontSize: { sm: "1rem", md: "1rem" }, fontWeight: 700, background: "linear-gradient(150deg, var(--roadmap-brand-a), var(--roadmap-brand-b))", backgroundClip: "text", color: "transparent" }}>
                        {card.release}
                      </Typography>
                    )}

                    {card.badge && (
                      <Typography component="span" sx={{ alignSelf: "flex-start", mt: 0.25, ml: "21.5%", color: "var(--roadmap-soft)", fontFamily: '"Geist", Helvetica, Arial, sans-serif', fontSize: 11, fontWeight: 500, letterSpacing: ".02em" }}>
                        {card.badge}
                      </Typography>
                    )}
                    <Typography component="span" sx={{ position: "absolute", bottom: 1.75, color: "var(--roadmap-hint)", fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase" }}>
                      Hover / tap to flip
                    </Typography>
                  </Paper>

                  <Paper id={descriptionId} elevation={0} sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden", px: 2.75, py: 3, borderRadius: "18px", backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)", color: "#fff", background: "linear-gradient(rgba(15,8,20,.32), rgba(15,8,20,.32)), linear-gradient(150deg, var(--roadmap-brand-a), var(--roadmap-brand-b))", boxShadow: "0 16px 40px -22px var(--roadmap-card-shadow)" }}>
                    {card.image?.src && (
                      <CdnImage
                        src={resolveAssetUrl(card.image.src)}
                        alt=""
                        sx={{ width: "auto", maxWidth: "100%", height: 66, mb: 1.75, objectFit: "contain", filter: "brightness(0) invert(1)", opacity: .95 }}
                      />
                    )}
                    {!card.image?.src && (
                      <Typography component="span" sx={{ mb: 1.25, fontSize: "1.2rem", fontWeight: 700 }}>
                        {card.release
                          ? `${card.release} : ${card.title}`
                          : card.title
                        }
                      </Typography>
                    )}
                    <Typography component="span" sx={{ color: "rgba(255,255,255,.9)", fontSize: 13.5, lineHeight: 1.5 }}>{card.body}</Typography>
                  </Paper>
                </Box>
              </ButtonBase>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}
